<div class="flex gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
  <div class="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-500">
    <app-icon class="inline-flex !h-[18px] !w-[18px] items-center justify-center !text-[18px] !leading-[18px]">{{ icon }}</app-icon>
  </div>
  <div class="min-w-0 flex-1">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="min-w-0">
        <div class="truncate text-sm font-semibold text-slate-800">{{ titleKey | translate }}</div>
        <div class="mt-0.5 flex min-w-0 items-center gap-1.5">
          <app-avatar
            size="16px"
            [title]="getTimelineAuthorName()"
            [url]="getTimelineAuthorAvatarUrl()"
            [isPhotoGallery]="false"
          />
          <span class="truncate text-xs font-medium text-slate-500">{{ getTimelineAuthorName() }}</span>
        </div>
      </div>
      <div class="text-xs text-slate-400">{{ event.created_at | date: 'dd MMM HH:mm' }}</div>
    </div>

    @if (contactLink; as link) {
      <div class="mt-2 flex">
        <button
          type="button"
          class="inline-flex max-w-full items-center gap-1.5 rounded-full! bg-white py-0.5 pl-0.5 pr-2.5 text-sm font-medium ring-1 ring-slate-200 transition hover:ring-orange-200 hover:text-orange-600"
          [ngClass]="link.linked ? 'text-slate-700' : 'text-slate-400 line-through'"
          [title]="link.name"
          (click)="openContact(link.contact)"
        >
          <app-avatar size="20px" [title]="link.name" [isPhotoGallery]="false" />
          <span class="truncate">{{ link.name }}</span>
        </button>
      </div>
    }

    @if (companyLink; as link) {
      <div class="mt-2 flex min-w-0 flex-wrap items-center gap-1.5">
        @if (link.old) {
          <ng-container [ngTemplateOutlet]="companyChip" [ngTemplateOutletContext]="{ $implicit: link.old, removed: true }" />
        }
        @if (link.old && link.new) {
          <app-icon class="!h-4 !w-4 !text-base !leading-none text-slate-400">arrow_right_alt</app-icon>
        }
        @if (link.new) {
          <ng-container [ngTemplateOutlet]="companyChip" [ngTemplateOutletContext]="{ $implicit: link.new, removed: false }" />
        }
      </div>
    }

    @if (cardLink; as link) {
      <div class="mt-2 flex">
        <span
          class="inline-flex max-w-full items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-medium ring-1 ring-slate-200"
          [ngClass]="link.linked ? 'text-slate-700' : 'text-slate-400 line-through'"
          [title]="link.card.name"
        >
          <app-icon class="!h-3.5 !w-3.5 !text-sm !leading-none text-slate-400">view_kanban</app-icon>
          <span class="truncate">{{ link.card.name }}</span>
        </span>
      </div>
    }

    @if (mergedCards.length) {
      <div class="mt-2 flex flex-wrap items-center gap-1.5">
        <span class="text-xs font-medium text-slate-400">{{ 'timeline_merged_cards' | translate }}:</span>
        @for (card of mergedCards; track card.id) {
          <span
            class="inline-flex max-w-full items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200"
            [title]="card.name"
          >
            <app-icon class="!h-3.5 !w-3.5 !text-sm !leading-none text-slate-400">description</app-icon>
            <span class="truncate">{{ card.name }}</span>
          </span>
        }
      </div>
    }

    @if (diffRows.length) {
      <div class="mt-2 flex flex-col gap-1 text-sm text-slate-600">
        @for (row of diffRows; track row.label) {
          <ng-container [ngTemplateOutlet]="diffRow" [ngTemplateOutletContext]="{ $implicit: row }" />
        }
      </div>
    }

    @if (customFieldDiffRows.length) {
      <div class="mt-3 flex items-center gap-2" [class.border-t]="diffRows.length" [class.border-slate-200]="diffRows.length" [class.pt-2]="diffRows.length">
        <app-icon class="!h-3.5 !w-3.5 !text-sm !leading-none text-slate-400">list_alt</app-icon>
        <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">{{ 'timeline_custom_fields' | translate }}</span>
      </div>
      <div class="mt-1 flex flex-col gap-1 text-sm text-slate-600">
        @for (row of customFieldDiffRows; track row.label) {
          <ng-container [ngTemplateOutlet]="diffRow" [ngTemplateOutletContext]="{ $implicit: row }" />
        }
      </div>
    }
  </div>
</div>

<ng-template #diffRow let-row>
  <div class="flex flex-wrap items-center gap-1.5">
    <span class="text-xs font-medium text-slate-400">{{ row.translateLabel ? (row.label | translate) : row.label }}:</span>
    @switch (row.status) {
      @case ('added') {
        <span class="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700">{{ 'timeline_added' | translate }}</span>
        @if (row.newAvatarUrl || row.isPerson) {
          <app-avatar size="18px" [title]="row.new" [url]="row.newAvatarUrl" [isPhotoGallery]="false" />
        }
        @if (row.newColor) {
          <ng-container [ngTemplateOutlet]="stageTag" [ngTemplateOutletContext]="{ $implicit: row.new, color: row.newColor }" />
        } @else {
          <span class="font-medium text-slate-700">{{ row.new }}</span>
        }
      }
      @case ('removed') {
        <span class="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">{{ 'timeline_removed' | translate }}</span>
        @if (row.oldAvatarUrl || row.isPerson) {
          <app-avatar size="18px" [title]="row.old" [url]="row.oldAvatarUrl" [isPhotoGallery]="false" />
        }
        @if (row.oldColor) {
          <span class="opacity-60"><ng-container [ngTemplateOutlet]="stageTag" [ngTemplateOutletContext]="{ $implicit: row.old, color: row.oldColor, strikethrough: true }" /></span>
        } @else {
          <span class="text-slate-400 line-through">{{ row.old }}</span>
        }
      }
      @default {
        @if (row.oldAvatarUrl || row.isPerson) {
          <app-avatar size="18px" [title]="row.old" [url]="row.oldAvatarUrl" [isPhotoGallery]="false" />
        }
        @if (row.oldColor) {
          <span class="opacity-60"><ng-container [ngTemplateOutlet]="stageTag" [ngTemplateOutletContext]="{ $implicit: row.old, color: row.oldColor, strikethrough: true }" /></span>
        } @else {
          <span class="text-slate-400 line-through">{{ row.old }}</span>
        }
        <app-icon class="!h-4 !w-4 !text-base !leading-none">arrow_right_alt</app-icon>
        @if (row.newAvatarUrl || row.isPerson) {
          <app-avatar size="18px" [title]="row.new" [url]="row.newAvatarUrl" [isPhotoGallery]="false" />
        }
        @if (row.newColor) {
          <ng-container [ngTemplateOutlet]="stageTag" [ngTemplateOutletContext]="{ $implicit: row.new, color: row.newColor }" />
        } @else {
          <span class="font-medium text-slate-700">{{ row.new }}</span>
        }
      }
    }
  </div>
</ng-template>

<ng-template #companyChip let-company let-removed="removed">
  <button
    type="button"
    class="inline-flex max-w-full items-center gap-1.5 rounded-full! bg-white py-0.5 pl-0.5 pr-2.5 text-sm font-medium ring-1 ring-slate-200 transition hover:ring-orange-200 hover:text-orange-600"
    [ngClass]="removed ? 'text-slate-400 line-through' : 'text-slate-700'"
    [title]="company.name ?? ''"
    (click)="openCompany(company)"
  >
    <span class="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500">
      <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">apartment</app-icon>
    </span>
    <span class="truncate">{{ company.name || '—' }}</span>
  </button>
</ng-template>

<ng-template #stageTag let-value let-color="color" let-strikethrough="strikethrough">
  <span
    class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
    [style.background]="'color-mix(in srgb, ' + color + ' 18%, white)'"
  >
    <span class="h-1.5 w-1.5 rounded-full" [style.background-color]="color"></span>
    <span [class.line-through]="strikethrough" [style.color]="color">{{ value }}</span>
  </span>
</ng-template>
